import { FC } from 'react';
import classNames from 'classnames';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import './CaseStudiesCard.scss';

import { GothamBook } from '@/utils/fonts';

import { TypeCaseStudyWithoutUnresolvableLinksResponse } from '../_generated';
import Icon from '@/components/Icon/Icon';
import CustomImage from '@/components/CustomImage/CustomImage';

export type TLabels = {
    learnMore: string;
    filters: string;
    apply: string;
    clearAll: string;
    byDivision: string;
    bySector: string;
};

type TCaseStudiesCardProps = {
    learnMorelabel?: string;
    onTitleClick?: (title: string) => void;
    onSubtitleClick?: (subTitle: string) => void;
    onLearnMoreClick?: () => void;
    item: TypeCaseStudyWithoutUnresolvableLinksResponse;
    hidePreview?: boolean;
    isShortView?: boolean;
};

const CaseStudiesCard: FC<TCaseStudiesCardProps> = ({
    onTitleClick,
    onSubtitleClick,
    onLearnMoreClick = () => {},
    learnMorelabel = '',
    item,
    hidePreview = false,
    isShortView,
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: item.sys.id });
    return (
        <div className="case-studies-card tw-bg-[#f1f4f7] tw-relative tw-w-full tw-rounded-lg tw-border-[0.5px] tw-border-gray-light tw-overflow-hidden lg:tw-max-w-[251px]">
            <div className="tw-w-full tw-overflow-hidden tw-bg-white tw-border-bl-50-15 tw-border-br-50-15">
                {item.fields.logo?.fields.file?.url ? (
                    <CustomImage
                        src={`https:${item.fields.logo.fields.file.url}?fm=webp`}
                        alt={item.fields.logo.fields.title ?? item.fields.division1}
                        width={156}
                        height={72}
                        className="tw-h-[110px] tw-w-auto tw-py-[20px] tw-mx-auto"
                        {...(hidePreview
                            ? {}
                            : inspectorProps({
                                  fieldId: 'logo',
                              }))}
                    />
                ) : (
                    <div className="tw-py-[20px] tw-mx-auto tw-h-[112px]" />
                )}
            </div>
            <div className="tw-p-[15px] tw-pb-[32px] tw-min-h-[210px]">
                <div className="tw-pb-2">
                    <h2 className="tw-font-bold tw-pb-0 tw-text-[#003865] tw-text-[13px] tw-tracking-wide tw-leading-5  tw-transition-all tw-duration-300">
                        <span
                            className={classNames({
                                'tw-cursor-pointer hover:tw-underline': !isShortView,
                            })}
                            onClick={
                                isShortView
                                    ? undefined
                                    : () => onTitleClick?.(item.fields.division1)
                            }
                            {...(hidePreview
                                ? {}
                                : inspectorProps({
                                      fieldId: isShortView ? 'title' : 'division1',
                                  }))}
                        >
                            {isShortView ? item.fields.title : item.fields.division1}
                        </span>
                    </h2>
                    {!isShortView && (
                        <h3 className="tw-font-bold tw-text-[#007CB9] tw-pb-[10px] tw-text-[13px] tw-tracking-wide tw-leading-5 tw-transition-all tw-duration-300">
                            <div
                                className="tw-cursor-pointer hover:tw-underline"
                                onClick={() => onSubtitleClick?.(item.fields.category)}
                                {...(hidePreview
                                    ? {}
                                    : inspectorProps({
                                          fieldId: 'category',
                                      }))}
                            >
                                {item.fields.category}
                            </div>
                        </h3>
                    )}
                    <p
                        className={classNames(
                            GothamBook.className,
                            'tw-text-[#4B4F54]  tw-min-h-[80px] tw-text-[13px] tw-font-gotham tw-font-normal tw-tracking-wide tw-pb-1 tw-leading-5 ',
                            { 'tw-pt-4': isShortView },
                        )}
                        {...(hidePreview
                            ? {}
                            : inspectorProps({
                                  fieldId: 'subDescription',
                              }))}
                    >
                        {item.fields.subDescription}
                    </p>
                </div>
                <div className="tw-absolute tw-bottom-[15px] tw-flex tw-justify-start tw-items-center">
                    <div
                        className="tw-text-[#ce0058] tw-uppercase tw-cursor-pointer tw-mr-1 tw-font-medium tw-text-[14px] tw-tracking-wide tw-leading-5 hover:tw-mr-4 tw-transition-all tw-duration-800"
                        onClick={onLearnMoreClick}
                    >
                        {learnMorelabel}
                    </div>
                    <Icon
                        type="angleRight"
                        width={20}
                        height={20}
                        className="tw-fill-[#ce0058] tw-w-[20px] tw-h-[20px] tw-pb-[2px] tw-text-[20px] tw-tracking-wide tw-font-gotham-bold"
                    />
                </div>
            </div>
        </div>
    );
};

export default CaseStudiesCard;
